import { Form } from "@unform/web";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import AdminRoutes from "Routes/Components/Admin";

import { HTTPErrorCallback } from "Helpers/Error";
import { transformIntoArrayOptions } from "Helpers/Format";
import { locations } from "Helpers/Locations";
import { RELATED_AREA_OPTIONS, ODS_OPTIONS } from "Helpers/Project";

import { UsePaginatedFetch } from "Hooks/UsePaginatedFetch";
import UseServices from "Hooks/UseServices";

import Button from "Components/Admin/Buttons";
import Checkbox from "Components/Admin/Form/Checkbox";
import Input from "Components/Admin/Form/Input";
import Select from "Components/Admin/Form/Select";
import Loading from "Components/Admin/Loading";
import Section from "Components/Admin/Section";
import Table from "Components/Admin/Table";
import { TableActionLink, withRowLink } from "Components/Admin/TableRowLink";

import { columns, downloadColumns } from "./columns";
import * as S from "./styles";

// Opções de Território = estados (o local de realização do projeto é gravado
// com o NOME do estado como chave, ex.: { "São Paulo": ["Campinas", ...] }).
const STATE_OPTIONS = locations.states.map((state) => ({
  value: state.name,
  label: state.name,
}));

const LINK_TITLE = "Abrir projeto — Ctrl/Cmd+clique para nova aba";

// Só o nome do PROJETO vira link. "Nome do Responsável" aponta para outra
// entidade (o usuário), cujo id não vem nesta listagem.
const detailPath = (row) =>
  row.id ? AdminRoutes.UpdateProjects.path.replace(":id", row.id) : null;

const ListProjects = () => {
  const location = useLocation();
  const { api } = UseServices();

  const [formData, setFormData] = useState({});
  const [sponsorOptions, setSponsorOptions] = useState([]);
  const [sponsoredChecked, setSponsoredChecked] = useState(false);

  const [selectedSponsor, setSelectedSponsor] = useState([]);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCommitments, setSelectedCommitments] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedTerritorio, setSelectedTerritorio] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  // Inicia como [] (e não null), igual a selectedSponsor: o Core do Select resolve
  // o valor com `options.find(o => o.value === value)`, então com null ele
  // auto-selecionaria a opção "Todos" (cujo value é null) e o placeholder nunca
  // apareceria. Array vazio é falsy para o react-select, mas truthy para o `||`.
  const [selectedStatus, setSelectedStatus] = useState([]);

  const cancelRequest = useRef(false);

  useEffect(() => {
    getSponsors();
    getCategories();
    getProjectStatuses();
    // Verificar se veio filtro do dashboard
    if (location.state?.sponsored) {
      setSponsoredChecked(true);
      setFormData({ sponsored: true });
    }
  }, []);

  const {
    setStartFetch,
    progress,
    requestInProgress,
    firstResponseReceived,
    data,
  } = UsePaginatedFetch({
    method: "post",
    path: "/projects/list-projects",
    body: { filters: { ...formData } },
    responseAccessor: "projects",
    pageSize: 1000,
    cancelRequest: cancelRequest,
  });

  useEffect(() => {
    // Carregar dados automaticamente ao abrir a tela ou quando vem filtro do dashboard
    if (!requestInProgress && !firstResponseReceived) {
      setStartFetch(true);
    }
    // eslint-disable-next-line
  }, [formData]);

  // Dispara busca automaticamente quando checkbox muda
  useEffect(() => {
    if (firstResponseReceived) {
      const newFormData = { ...formData };

      // Se marcado, adiciona o filtro; se desmarcado, remove
      if (sponsoredChecked) {
        newFormData.sponsored = true;
      } else {
        delete newFormData.sponsored;
      }

      setFormData(newFormData);
      setStartFetch(true);
    }
    // eslint-disable-next-line
  }, [sponsoredChecked]);

  const getSponsors = () => {
    // Lista as empresas patrocinadoras (novo modelo — tabela `sponsors`),
    // não mais os usuários com a flag `is_sponsor`.
    api
      .post("/sponsors/list", { filters: {} })
      .then((response) => {
        const sponsors = response.data?.sponsors;
        const options = transformIntoArrayOptions(sponsors, "id", "name");

        // Adiciona opção "Todos" no início para limpar o filtro
        setSponsorOptions([{ value: null, label: "Todos" }, ...options]);
      })
      .catch((error) => {
        return HTTPErrorCallback(error);
      });
  };

  const getCategories = () => {
    api
      .get("/projects/get-project-categories")
      .then((response) => {
        setCategoryOptions(
          transformIntoArrayOptions(response.data, "id", "display_name"),
        );
      })
      .catch((error) => {
        return HTTPErrorCallback(error);
      });
  };

  // Status do PROJETO (project_statuses: Cadastrado / Em captação / Em execução /
  // Finalizado). Reaproveita o catálogo que o formulário de projeto já consome —
  // não é o status do vínculo com o patrocinador, que vem de outra rota
  // (/projects/get-project-sponsor-statuses).
  const getProjectStatuses = () => {
    api
      .get("/projects/setup")
      .then((response) => {
        const options = transformIntoArrayOptions(
          response.data?.statuses,
          "id",
          "display_name",
        );

        // "Todos" no início para limpar o filtro.
        setStatusOptions([{ value: null, label: "Todos" }, ...options]);
      })
      .catch((error) => {
        return HTTPErrorCallback(error);
      });
  };

  return (
    <Section>
      <S.SectionWrapper>
        <S.Wrapper>
          <Form
            onSubmit={
              !requestInProgress
                ? (data) => {
                    const newFormData = { ...data };

                    // Filtro por empresa patrocinadora (novo modelo).
                    // Só mantém se houver valor válido (não for "Todos").
                    if (selectedSponsor?.value) {
                      newFormData.sponsor_company_id = selectedSponsor.value;
                    } else {
                      delete newFormData.sponsor_company_id;
                    }

                    // Só adiciona o filtro se estiver marcado
                    if (sponsoredChecked) {
                      newFormData.sponsored = true;
                    } else {
                      delete newFormData.sponsored;
                    }

                    // Filtros multivalor (controlados por estado).
                    if (selectedCategories.length) {
                      newFormData.category_ids = selectedCategories.map(
                        (option) => option.value,
                      );
                    } else {
                      delete newFormData.category_ids;
                    }

                    if (selectedCommitments.length) {
                      newFormData.project_commitments = selectedCommitments.map(
                        (option) => option.value,
                      );
                    } else {
                      delete newFormData.project_commitments;
                    }

                    if (selectedAreas.length) {
                      newFormData.related_areas = selectedAreas.map(
                        (option) => option.value,
                      );
                    } else {
                      delete newFormData.related_areas;
                    }

                    // Território = nome do estado (chave do local de realização).
                    if (selectedTerritorio?.value) {
                      newFormData.territorio = selectedTerritorio.value;
                    } else {
                      delete newFormData.territorio;
                    }

                    // Status do projeto ("Todos" tem value nulo e limpa o filtro).
                    if (selectedStatus?.value) {
                      newFormData.project_status_id = selectedStatus.value;
                    } else {
                      delete newFormData.project_status_id;
                    }

                    setFormData(newFormData);
                    setStartFetch(true);
                  }
                : null
            }
          >
            <Input
              name="id"
              label="ID do Projeto"
              placeholder="ID"
              type="text"
            />

            <Input
              name="title"
              label="Título do Projeto"
              placeholder="Título"
              type="text"
            />

            <Input
              name="localidade"
              label="Localidade"
              placeholder="Estado, cidade, bairro..."
              type="text"
            />

            <Select
              name="territorio"
              label="Território (local de realização)"
              options={STATE_OPTIONS}
              placeholder="Selecione o estado"
              value={selectedTerritorio}
              onChange={setSelectedTerritorio}
            />

            <Select
              name="category_ids"
              label="Lei de Incentivo"
              options={categoryOptions}
              placeholder="Selecione as leis"
              value={selectedCategories}
              onChange={setSelectedCategories}
              isMulti
            />

            <Select
              name="project_status_id"
              label="Status do projeto"
              options={statusOptions}
              placeholder="Selecione o status"
              value={selectedStatus}
              onChange={setSelectedStatus}
            />

            <Input
              name="period_start"
              label="Período do projeto (data inicial)"
              type="date"
            />

            <Input
              name="period_finish"
              label="Período do projeto (data final)"
              type="date"
            />

            <Select
              name="project_commitments"
              label="Compromissos ODS"
              options={ODS_OPTIONS}
              placeholder="Selecione os ODS"
              value={selectedCommitments}
              onChange={setSelectedCommitments}
              isMulti
            />

            <Select
              name="related_areas"
              label="Área relacionada"
              options={RELATED_AREA_OPTIONS}
              placeholder="Selecione as áreas"
              value={selectedAreas}
              onChange={setSelectedAreas}
              isMulti
            />

            <Select
              name="sponsor_company_id"
              label="Patrocinador"
              options={sponsorOptions}
              placeholder="Patrocinador"
              value={selectedSponsor}
              onChange={setSelectedSponsor}
            />

            <Checkbox
              name="sponsored"
              label="Apenas Patrocinados"
              defaultChecked={sponsoredChecked}
              onChange={(e) => setSponsoredChecked(e.target.checked)}
            />

            <Button
              type="submit"
              text={
                requestInProgress
                  ? progress
                    ? `Carregando ${progress}%...`
                    : "Carregando..."
                  : "Buscar"
              }
            />
            {requestInProgress && (
              <Button
                text="Cancelar requisição"
                variant="critical"
                onClick={() => {
                  cancelRequest.current = true;
                }}
              />
            )}
          </Form>

          {requestInProgress && !firstResponseReceived ? (
            <Loading />
          ) : (
            <Table
              name="Projetos"
              data={data}
              columns={[
                ...withRowLink(columns, {
                  accessor: "project_name",
                  to: detailPath,
                }),
                {
                  Header: "Ações",
                  accessor: "actions",
                  noSearch: true,
                  width: "70px",
                  Cell: ({ cell }) => (
                    <TableActionLink
                      to={detailPath(cell.row.original)}
                      title={LINK_TITLE}
                    />
                  ),
                },
              ]}
              downloadColumns={downloadColumns}
              disableDownloadButton={requestInProgress}
            />
          )}
        </S.Wrapper>
      </S.SectionWrapper>
    </Section>
  );
};

export default ListProjects;
